import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Spinner, Modal, Form } from 'react-bootstrap';
import {
  useApproveEntrepreneurProvisionMutation,
  useGetEntrepreneurFeePreviewQuery,
} from '../../app/api';
import { brand, usd } from './common';

/* Seat-activation charge + confirm modal, shared by the cross-firm Provisioning
 * inbox and the per-firm drill-down. Collects Claude's prorated mid-cycle charge
 * + tax (both entered by hand from the Anthropic console — proration isn't
 * available via any API), live-previews our Stripe processing-fee pass-through,
 * then charges the firm's card and confirms the seat as added in one step.
 * Charging happens server-side; this only submits once the employee accepts the
 * total shown. */

const toCents = (dollars) => {
  const n = parseFloat(dollars);
  return Number.isFinite(n) && n >= 0 ? Math.round(n * 100) : null;
};

export default function ActivationChargeModal({ member, onClose, onCharged }) {
  const [proratedInput, setProratedInput] = useState('');
  const [taxInput, setTaxInput] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [approveProvision] = useApproveEntrepreneurProvisionMutation();

  // No-charge override: confirm the seat without any charge (e.g. many licenses
  // added and billed together). Forced on when the firm has no card on file,
  // since there's then no way to charge — you can only confirm.
  const hasCard = member.firm_billing_ready !== false;
  const [noCharge, setNoCharge] = useState(!hasCard);

  const proratedCents = toCents(proratedInput);
  const taxCents = toCents(taxInput || '0');
  const amountsValid = proratedCents !== null && taxCents !== null && (proratedCents + taxCents) > 0;

  const { data: preview, isFetching: previewLoading } = useGetEntrepreneurFeePreviewQuery(
    { prorated_cents: proratedCents || 0, tax_cents: taxCents || 0 },
    { skip: noCharge || !amountsValid },
  );

  const canSubmit = noCharge ? true : (amountsValid && !previewLoading);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await approveProvision(
        noCharge
          ? { uuid: member.uuid, no_charge: true, note: note || undefined }
          : { uuid: member.uuid, prorated_cents: proratedCents, tax_cents: taxCents, note: note || undefined },
      ).unwrap();
      onCharged();
    } catch (err) {
      setError(err?.data?.detail || 'Could not confirm this seat. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal show onHide={submitting ? undefined : onClose} centered>
      <Modal.Header closeButton={!submitting}>
        <Modal.Title style={{ fontSize: '1.1rem' }}>
          {noCharge ? 'Confirm added to Claude' : 'Charge seat activation'} — {member.email}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p style={{ color: brand.charcoal, fontSize: '0.85rem' }}>
          {noCharge
            ? <>Confirm <strong>{member.email}</strong> ({member.firm_name || 'this firm'}) as added to Claude <strong>without any charge</strong>. Use this when the license was already billed together with others.</>
            : <>Enter the prorated mid-cycle charge and tax from the Anthropic console for adding <strong>{member.email}</strong> ({member.firm_name || 'this firm'}). We&apos;ll add our processing-fee pass-through and charge the firm&apos;s card on file.</>}
        </p>

        <Form.Check
          type="checkbox"
          id="activation-no-charge"
          className="mb-3"
          label={hasCard ? 'Confirm without charging the firm (no activation charge)' : 'No card on file — confirm without a charge'}
          checked={noCharge}
          disabled={submitting || !hasCard}
          onChange={(e) => setNoCharge(e.target.checked)}
        />

        {!noCharge && (
          <>
            <Form.Group className="mb-2">
              <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600 }}>Prorated Claude charge ($)</Form.Label>
              <Form.Control type="number" min="0" step="0.01" value={proratedInput} onChange={(e) => setProratedInput(e.target.value)} placeholder="0.00" autoFocus disabled={submitting} />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600 }}>Tax ($)</Form.Label>
              <Form.Control type="number" min="0" step="0.01" value={taxInput} onChange={(e) => setTaxInput(e.target.value)} placeholder="0.00" disabled={submitting} />
            </Form.Group>
          </>
        )}

        <Form.Group className="mb-3">
          <Form.Label style={{ fontSize: '0.8rem', fontWeight: 600 }}>Note (optional)</Form.Label>
          <Form.Control type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. invoice # from Anthropic" disabled={submitting} />
        </Form.Group>

        {!noCharge && (
          <div style={{ background: brand.softGray, borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.85rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Prorated charge</span><span>{proratedCents !== null ? usd(proratedCents) : '—'}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Tax</span><span>{taxCents !== null ? usd(taxCents) : '—'}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between', color: brand.charcoal }}>
              <span>Processing fee</span>
              <span>{amountsValid ? (previewLoading ? <Spinner size="sm" animation="border" /> : usd(preview?.fee_cents || 0)) : '—'}</span>
            </div>
            <hr className="my-2" />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: brand.deepBlue, fontSize: '0.95rem' }}>
              <span>Total to charge</span>
              <span>{amountsValid ? (previewLoading ? '…' : usd(preview?.total_cents || 0)) : '—'}</span>
            </div>
          </div>
        )}

        {error && <div style={{ color: brand.red, fontSize: '0.82rem', marginTop: '0.75rem' }}>{error}</div>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="success" onClick={handleSubmit} disabled={!canSubmit || submitting}>
          {submitting
            ? <><Spinner size="sm" animation="border" className="me-1" />{noCharge ? 'Confirming…' : 'Charging…'}</>
            : noCharge
              ? 'Confirm added (no charge)'
              : `Charge ${amountsValid && preview ? usd(preview.total_cents) : ''} and confirm`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
ActivationChargeModal.propTypes = {
  member: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    email: PropTypes.string,
    firm_name: PropTypes.string,
    firm_billing_ready: PropTypes.bool,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onCharged: PropTypes.func.isRequired,
};
